/* eslint-disable complexity */
import type { Topic, UpsertTopicInput, EntityType } from '@communities/content-management/domain';
import { useUpsertCommunityGroupTopic, useUpsertCommunityTopic } from '@communities/content-management/hooks';
import { isCommunity } from '@communities/content-management/utils';
import { ErrorBanner } from '@core/components';
import { TextInput, LoadingSpinner, Dialog, Switch } from '@soteria/design-system-ve';
import { useState, useEffect } from 'react';

interface TopicDialogProps {
  topic?: Topic | undefined;
  entityId: string;
  entityType: EntityType;
  isOpen: boolean;
  onClose: () => void;
  editMode?: 'name' | 'moderation' | 'strike' | 'all';
}

export function TopicDialog({ topic: initialTopic, entityId, entityType, isOpen, onClose, editMode = 'all' }: TopicDialogProps) {
  const [currentTopic, setCurrentTopic] = useState<Topic | undefined>(initialTopic);

  const communityHook = useUpsertCommunityTopic(isCommunity(entityType) ? entityId : undefined);
  const communityGroupHook = useUpsertCommunityGroupTopic(!isCommunity(entityType) ? entityId : undefined);

  const { upsertTopic, loading: mutating, errors, resetErrors } = isCommunity(entityType) ? communityHook : communityGroupHook;

  useEffect(() => {
    if (isOpen) {
      resetErrors();
      setCurrentTopic(initialTopic);
    }
  }, [initialTopic, isOpen, resetErrors]);

  const handleFieldChange = (field: keyof Topic, value: unknown) => {
    setCurrentTopic(prev => ({
      name: prev?.name || '',
      humanModerationAssignedTtlInSeconds: prev?.humanModerationAssignedTtlInSeconds || 0,
      maxCasesPerModerator: prev?.maxCasesPerModerator || 0,
      humanModerationCasesSlaInSeconds: prev?.humanModerationCasesSlaInSeconds || 0,
      isStrikeEnabled: prev?.isStrikeEnabled ?? false,
      ...prev,
      [field]: value,
    }));
  };

  const buildUpsertInput = (): UpsertTopicInput => {
    const input: UpsertTopicInput = {};

    // Include moderation fields if they have values
    if (currentTopic?.humanModerationAssignedTtlInSeconds) {
      input.humanModerationAssignedTtlInSeconds = Number(currentTopic.humanModerationAssignedTtlInSeconds);
    }

    if (currentTopic?.maxCasesPerModerator) {
      input.maxCasesPerModerator = Number(currentTopic.maxCasesPerModerator);
    }

    if (currentTopic?.humanModerationCasesSlaInSeconds) {
      input.humanModerationCasesSlaInSeconds = Number(currentTopic.humanModerationCasesSlaInSeconds);
    }

    // Include strike enabled flag when in strike or all mode
    if (editMode === 'strike' || editMode === 'all') {
      input.isStrikeEnabled = currentTopic?.isStrikeEnabled ?? false;
    }

    return input;
  };

  const handleSubmit = async () => {
    resetErrors();

    const input = buildUpsertInput();
    const success = await upsertTopic(entityId, currentTopic?.name || '', input);

    if (success) {
      onClose();
    }
  };

  return (
    <Dialog
      description={initialTopic ? 'Update topic settings' : 'Configure a new topic'}
      isOpen={isOpen}
      primaryAction={{
        label: mutating ? <LoadingSpinner /> : initialTopic ? 'Update Topic' : 'Create Topic',
        onClick: handleSubmit,
        disabled: mutating || !currentTopic?.name?.trim(),
      }}
      size="lg"
      title={initialTopic ? 'Edit Topic' : 'Add Topic'}
      onClose={onClose}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
        {/* Error Banner */}
        {errors && errors.length > 0 && <ErrorBanner errors={errors} title="There was an error saving the topic:" />}

        {/* Topic Name Input */}
        <TextInput
          required
          disabled={mutating || editMode === 'moderation' || (editMode === 'strike' && !!initialTopic)}
          id="topicName"
          label="Topic Name"
          placeholder="Enter topic name"
          value={currentTopic?.name || ''}
          onChange={value => handleFieldChange('name', value)}
        />

        {/* Strike Enabled Switch - only shown in strike mode */}
        {editMode === 'strike' && (
          <Switch checked={currentTopic?.isStrikeEnabled ?? false} label="Strike Enabled" onChange={checked => handleFieldChange('isStrikeEnabled', checked)} />
        )}

        {editMode !== 'name' && editMode !== 'strike' && (
          <>
            {/* Human Moderation Assigned TTL Input */}
            <TextInput
              disabled={mutating}
              helperText="Time in seconds before an assigned case is automatically unassigned"
              id="humanModerationAssignedTtlInSeconds"
              label="Human Moderation Assigned TTL (seconds)"
              min="1"
              placeholder="Enter TTL in seconds"
              type="number"
              value={currentTopic?.humanModerationAssignedTtlInSeconds ? currentTopic.humanModerationAssignedTtlInSeconds.toString() : ''}
              onChange={value => handleFieldChange('humanModerationAssignedTtlInSeconds', Number(value))}
            />

            {/* Max Cases Per Moderator Input */}
            <TextInput
              disabled={mutating}
              helperText="Maximum number of cases that can be assigned to a single moderator"
              id="maxCasesPerModerator"
              label="Max Cases Per Moderator"
              min="1"
              placeholder="Enter maximum cases"
              type="number"
              value={currentTopic?.maxCasesPerModerator ? currentTopic.maxCasesPerModerator.toString() : ''}
              onChange={value => handleFieldChange('maxCasesPerModerator', Number(value))}
            />

            {/* Human Moderation Cases SLA Input */}
            <TextInput
              disabled={mutating}
              helperText="Service Level Agreement time in seconds for moderation cases"
              id="humanModerationCasesSlaInSeconds"
              label="Human Moderation Cases SLA (seconds)"
              min="1"
              placeholder="Enter SLA in seconds"
              type="number"
              value={currentTopic?.humanModerationCasesSlaInSeconds ? currentTopic.humanModerationCasesSlaInSeconds.toString() : ''}
              onChange={value => handleFieldChange('humanModerationCasesSlaInSeconds', Number(value))}
            />
          </>
        )}
      </div>
    </Dialog>
  );
}
